import { useEffect } from 'react';
import { ART } from '../site/media';
import { metadataForPath } from '../static-metadata';

const PUBLIC_BASE = (import.meta.env.VITE_PUBLIC_BASE_URL || 'https://enshield.com').replace(/\/$/, '');
/** Approved homepage hero art, reused as the social preview. */
const SOCIAL_IMAGE = ART.homeHero.src;

function upsertMeta(selector: string, attributes: Record<string, string>) {
  let node = document.head.querySelector<HTMLMetaElement>(selector);
  if (!node) { node = document.createElement('meta'); document.head.appendChild(node); }
  Object.entries(attributes).forEach(([key, value]) => node!.setAttribute(key, value));
}

export function PageMeta({ title, description, noIndex = false }: { title: string; description: string; noIndex?: boolean }) {
  useEffect(() => {
    const canonicalPath = metadataForPath(window.location.pathname)?.canonicalPath ?? window.location.pathname;
    document.title = `${title} | Enshield`;
    upsertMeta('meta[name="description"]', { name: 'description', content: description });
    upsertMeta('meta[name="robots"]', { name: 'robots', content: noIndex ? 'noindex, nofollow' : 'index, follow' });
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: `${title} | Enshield` });
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: description });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: `${PUBLIC_BASE}${canonicalPath}` });
    upsertMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' });
    upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: 'Enshield' });
    // Without an image every share of an Enshield link renders as a bare text
    // card. Uses the approved homepage hero art rather than a new asset.
    upsertMeta('meta[property="og:image"]', { property: 'og:image', content: `${PUBLIC_BASE}${SOCIAL_IMAGE}` });
    upsertMeta('meta[property="og:image:alt"]', { property: 'og:image:alt', content: 'Enshield — protection that moves with commerce' });
    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: `${title} | Enshield` });
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description });
    upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: `${PUBLIC_BASE}${SOCIAL_IMAGE}` });
    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = `${PUBLIC_BASE}${canonicalPath}`;
  }, [description, noIndex, title]);
  return null;
}
